import type {
  AddressBreakdown,
  DeliveryLegCreateInput,
  DeliveryLegDefaults,
  DeliveryLegDto,
  DeliveryLegPatchInput,
  EdoLegStateDto,
  ExpeditorDocumentDto,
} from '@expeditor/shared';
import { apiClient } from './client';

export const deliveryLegsApi = {
  /** Сохранённая разбивка адреса по тексту (для предзаполнения формы ЭТрН). */
  addressBreakdown(raw: string): Promise<AddressBreakdown | null> {
    return apiClient
      .get<AddressBreakdown | null>('/delivery-legs/address-breakdown', { params: { raw } })
      .then((r) => r.data);
  },
  /** Разбивка адреса из карточки стороны: одна на текст адреса. */
  saveAddressBreakdown(raw: string, parts: AddressBreakdown): Promise<{ saved: boolean }> {
    return apiClient
      .put<{ saved: boolean }>('/delivery-legs/address-breakdown', { raw, parts })
      .then((r) => r.data);
  },
  defaults(shipmentId: string): Promise<DeliveryLegDefaults> {
    return apiClient
      .get<DeliveryLegDefaults>(`/shipments/${shipmentId}/delivery-legs/defaults`)
      .then((r) => r.data);
  },
  list(shipmentId: string): Promise<DeliveryLegDto[]> {
    return apiClient
      .get<DeliveryLegDto[]>(`/shipments/${shipmentId}/delivery-legs`)
      .then((r) => r.data);
  },
  create(shipmentId: string, dto: DeliveryLegCreateInput): Promise<DeliveryLegDto> {
    return apiClient
      .post<DeliveryLegDto>(`/shipments/${shipmentId}/delivery-legs`, dto)
      .then((r) => r.data);
  },
  /** Дефолты по плечу маршрута поручения (ЭТрН на авто-плечо и т.п.). */
  segmentDefaults(orderId: string, segmentId: string): Promise<DeliveryLegDefaults> {
    return apiClient
      .get<DeliveryLegDefaults>(
        `/orders/${orderId}/route-segments/${segmentId}/delivery-legs/defaults`,
      )
      .then((r) => r.data);
  },
  /** Дефолты по плечу ПЕРЕВОЗКИ (фаза 2). */
  legDefaults(shipmentId: string, legId: string): Promise<DeliveryLegDefaults> {
    return apiClient
      .get<DeliveryLegDefaults>(`/shipments/${shipmentId}/legs/${legId}/delivery-legs/defaults`)
      .then((r) => r.data);
  },
  listForOrder(orderId: string): Promise<DeliveryLegDto[]> {
    return apiClient.get<DeliveryLegDto[]>(`/orders/${orderId}/delivery-legs`).then((r) => r.data);
  },
  createForOrder(orderId: string, dto: DeliveryLegCreateInput): Promise<DeliveryLegDto> {
    return apiClient
      .post<DeliveryLegDto>(`/orders/${orderId}/delivery-legs`, dto)
      .then((r) => r.data);
  },
  patch(id: string, patch: DeliveryLegPatchInput): Promise<DeliveryLegDto> {
    return apiClient.patch<DeliveryLegDto>(`/delivery-legs/${id}`, patch).then((r) => r.data);
  },
  remove(id: string): Promise<{ deleted: true }> {
    return apiClient.delete<{ deleted: true }>(`/delivery-legs/${id}`).then((r) => r.data);
  },
  xml(id: string): Promise<{ fileName: string; xml: string }> {
    return apiClient
      .get<{ fileName: string; xml: string }>(`/delivery-legs/${id}/xml`)
      .then((r) => r.data);
  },
  /**
   * Подписать ЭТрН в ЭДО.МИГ24: создаёт перевозку и возвращает ссылку и валидацию.
   * По умолчанию режим Strict — при ошибках перевозка не создаётся (`created:false`).
   * `force` → создать несмотря на ошибки (Deferred, отложенная проверка).
   */
  signEdo(
    id: string,
    force = false,
  ): Promise<{
    signUrl: string | null;
    transportationId: string | null;
    validation: { isValid: boolean; errors: string[]; warnings: string[] } | null;
    created: boolean;
  }> {
    return apiClient
      .post<{
        signUrl: string | null;
        transportationId: string | null;
        validation: { isValid: boolean; errors: string[]; warnings: string[] } | null;
        created: boolean;
      }>(`/delivery-legs/${id}/edo/sign`, undefined, force ? { params: { force: true } } : undefined)
      .then((r) => r.data);
  },

  /** Экспедиторские документы плеча (Фаза 2). */
  documents(id: string): Promise<ExpeditorDocumentDto[]> {
    return apiClient.get<ExpeditorDocumentDto[]>(`/delivery-legs/${id}/documents`).then((r) => r.data);
  },
  /** XML сохранённого документа плеча (для скачивания). */
  documentXml(id: string, docId: string): Promise<{ fileName: string; xml: string }> {
    return apiClient
      .get<{ fileName: string; xml: string }>(`/delivery-legs/${id}/documents/${docId}/xml`)
      .then((r) => r.data);
  },
  /** Выпустить поручение исполнителю (сохранить исходящий документ). */
  issuePoruchenie(id: string): Promise<ExpeditorDocumentDto[]> {
    return apiClient
      .post<ExpeditorDocumentDto[]>(`/delivery-legs/${id}/issue-poruchenie`)
      .then((r) => r.data);
  },
  /** Выпустить ЭЗЗ (электронный заказ/заявка) на плечо. */
  issueZakzvGo(id: string): Promise<ExpeditorDocumentDto[]> {
    return apiClient
      .post<ExpeditorDocumentDto[]>(`/delivery-legs/${id}/issue-zakzv`)
      .then((r) => r.data);
  },
  /** Принять расписку от исполнителя (входящий EXPRASP). */
  receiveRaspiska(id: string, xml: string, note?: string): Promise<ExpeditorDocumentDto[]> {
    return apiClient
      .post<ExpeditorDocumentDto[]>(`/delivery-legs/${id}/receive-raspiska`, { xml, note })
      .then((r) => r.data);
  },
  /** Отозвать поручение исполнителю (POREXPOTZ). */
  revokePoruchenie(id: string): Promise<ExpeditorDocumentDto[]> {
    return apiClient
      .post<ExpeditorDocumentDto[]>(`/delivery-legs/${id}/revoke-poruchenie`)
      .then((r) => r.data);
  },
  /** Синхронизировать состояние плеча из ЭДО.МИГ24 (статус + титулы/расписки). */
  edoState(id: string): Promise<EdoLegStateDto> {
    return apiClient.get<EdoLegStateDto>(`/delivery-legs/${id}/edo-state`).then((r) => r.data);
  },
};
